import {useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {CheckCircle} from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  Typography,
} from '@mui/material';

import OnboardingSection from './OnboardingSection';
import {LinkedInButton} from '../../../../components/LinkedInButton/LinkedInButton';
import {greenColors, orangeColors} from '../../../../layouts/theme/designSystem';
import {getLogo} from '../../../../layouts/theme/Utils';
import {AppConfig} from '../../../../lib/AppConfig';
import {OAUTH_PROVIDER_LINKEDIN} from '../../../../lib/constants';
import {OAuthService} from '../../../../services/oauth-service';

export default function LinkedinConnection() {
  const {t} = useTranslation();
  const preLoadedProfileUrl = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_PUBLIC_PROFILE_URL);
  const preLoadedProfilePictureUrl = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_PUBLIC_PICTURE_URL);

  const [linkedinData, setLinkedinData] = useState({
    publicProfileUrl: '',
    profilePictureUrl: '',
  });

  const [isBackDropOpen, setIsBackDropOpen] = useState(false);
  const [isLinkedInIntegrationInProgress, setIsLinkedInIntegrationInProgress] = useState(false);
  const [isStepOneCompleted, setIsStepOneCompleted] = useState(false);
  const [isStepTwoCompleted, setIsStepTwoCompleted] = useState(false);
  const handleCloseBackdrop = () => {
    if (!isLinkedInIntegrationInProgress || stepError > 0) setIsBackDropOpen(false);
  };
  const [isAuthorizationComplete, setIsAuthorizationComplete] = useState(false);
  const [stepError, setStepError] = useState(0);

  const LinkedInButtonStyles: SxProps = {
    backgroundColor: orangeColors[4],
    border: `1px solid ${orangeColors[3]}`,
    width: '400px',
    display: 'flex',
    color: greenColors[900],
    justifyContent: 'center',
    alignItems: 'center',
    padding: 1,
    '&:hover': {
      backgroundColor: 'white',
      borderColor: orangeColors[70],
      boxShadow: 'none',
    },
  };

  const getTitle = () => {
    if (linkedinData.publicProfileUrl || preLoadedProfileUrl) {
      return t('applicantOnboarding.linkedinConnection.titleConnected');
    } else if (stepError > 0) {
      return t('applicantOnboarding.linkedinConnection.titleError');
    }

    return t('applicantOnboarding.linkedinConnection.title');
  };

  useEffect(() => {
    if (isLinkedInIntegrationInProgress) {
      let authorizationCode = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
      let linkedinState = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);
      let errorMessage = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_ERROR_KEY);

      const authorizationCheck = setInterval(() => {
        authorizationCode = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
        linkedinState = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);
        errorMessage = localStorage.getItem(AppConfig.LOCAL_STORAGE_LINKEDIN_ERROR_KEY);

        if (errorMessage !== null) {
          setStepError(1);

          setTimeout(() => {
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_ERROR_KEY);
            clearInterval(authorizationCheck);
            setIsLinkedInIntegrationInProgress(false);
            setIsBackDropOpen(false);
          }, 5000);
        }

        if (authorizationCode && linkedinState) {
          clearInterval(authorizationCheck);
          setIsAuthorizationComplete(true);
          setIsStepOneCompleted(true);
        }
      }, 2000);

      if (isAuthorizationComplete && authorizationCode && linkedinState) {
        OAuthService.callback(OAUTH_PROVIDER_LINKEDIN, authorizationCode, linkedinState).then((res) => {
          if (res.ok) {
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);

            setIsStepTwoCompleted(true);

            setLinkedinData({
              publicProfileUrl: (res.data as any).publicProfileUrl,
              profilePictureUrl: (res.data as any).profilePictureUrl,
            });

            setIsLinkedInIntegrationInProgress(false);
            setIsBackDropOpen(false);
          } else {
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_AUTH_CODE_KEY);
            localStorage.removeItem(AppConfig.LOCAL_STORAGE_LINKEDIN_STATE_KEY);

            setStepError(2);

            setTimeout(() => {
              setIsLinkedInIntegrationInProgress(false);
              setIsBackDropOpen(false);
            }, 10000);
          }
        });
      }

      return () => {
        if (!isAuthorizationComplete) clearInterval(authorizationCheck);
      };
    }
  }, [isAuthorizationComplete, isLinkedInIntegrationInProgress]);

  const handleButtonClick = () => {
    setIsLinkedInIntegrationInProgress(true);
    setIsBackDropOpen(true);

    OAuthService.authorize(OAUTH_PROVIDER_LINKEDIN).then((res) => {
      if (res.ok) {
        window.open(res.data?.providerAuthorizationUrl, '_blank');
      } else {
        setStepError(1);

        setTimeout(() => {
          setIsLinkedInIntegrationInProgress(false);
          setIsBackDropOpen(false);
        }, 5000);
      }
    });
  };

  return (
    <OnboardingSection title={getTitle()}>
      {(linkedinData.profilePictureUrl || preLoadedProfilePictureUrl) && (
        <Card
          elevation={0}
          sx={{
            backgroundColor: 'background.paper',
          }}
        >
          <Container
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CardContent>
              <Avatar
                src={linkedinData.profilePictureUrl || preLoadedProfilePictureUrl || ''}
                sx={{width: 160, height: 160}}
                style={{transition: 'all 1s'}}
              />
            </CardContent>
          </Container>
        </Card>
      )}
      {(linkedinData.publicProfileUrl || preLoadedProfileUrl) && (
        <Box sx={{mt: 2}}>
          <Typography variant='body1' textAlign={'center'}>
            <Link
              href={linkedinData.publicProfileUrl || preLoadedProfileUrl || ''}
              variant='body1'
              color='secondary'
              target='_blank'
            >
              {linkedinData.publicProfileUrl || preLoadedProfileUrl}
            </Link>
          </Typography>
        </Box>
      )}

      {!isAuthorizationComplete && !preLoadedProfileUrl && (
        <Box display='flex' justifyContent={'center'}>
          <LinkedInButton
            onClick={handleButtonClick}
            sx={LinkedInButtonStyles}
            text={t('shared.socialSignin.importProfile')}
          />
        </Box>
      )}
      <Dialog
        open={isBackDropOpen}
        onClose={handleCloseBackdrop}
        sx={{
          '& .MuiDialog-paper': {
            backgroundImage: 'none',
          },
          p: 3,
        }}
      >
        <Box
          display={'flex'}
          sx={{
            justifyContent: {xs: 'center', md: 'center'},
            p: 3,
          }}
        >
          <Box component={'img'} src={getLogo()} alt='Empion Logo' />
        </Box>
        <DialogTitle textAlign={'center'}>{t('applicantOnboarding.linkedinConnection.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography component='span' variant='h4' textAlign={'center'}>
              {t('applicantOnboarding.linkedinConnection.dialog.content')}
            </Typography>
          </DialogContentText>
          <>
            <Divider sx={{my: 2}} />
            {stepError > 0 && (
              <Typography component={'span'} variant='h5' color={'error'} textAlign={'center'}>
                {t('applicantOnboarding.linkedinConnection.dialog.error')}
              </Typography>
            )}
            <List dense={true}>
              <ListItem component={'span'}>
                <ListItemIcon>
                  {isStepOneCompleted ? <CheckCircle /> : stepError === 1 ? <ErrorIcon /> : <CircularProgress />}
                </ListItemIcon>
                <ListItemText primary={t('applicantOnboarding.linkedinConnection.dialog.authorization')} />
              </ListItem>
              <ListItem component={'span'}>
                <ListItemIcon>
                  {isStepTwoCompleted ? <CheckCircle /> : stepError === 2 ? <ErrorIcon /> : <CircularProgress />}
                </ListItemIcon>
                <ListItemText primary={t('applicantOnboarding.linkedinConnection.dialog.connection')} />
              </ListItem>
            </List>
          </>
        </DialogContent>
      </Dialog>
    </OnboardingSection>
  );
}
